import React, { useCallback, useState, useEffect, useRef } from "react";
import Checkbox from "./Checkbox";
import produce from "immer";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import ValidationBar from "../common3/ValidationBar";
import HelpIcon from "../common3/HelpBox/Icon";
import HelpBox from "../common3/HelpBox";
import filterString from "../common3/filterString";
import SkeletonLoader from "../Common/Loaders/SkeletonLoader";
import HorizontalCheckboxGroup from "./HorizontalCheckboxGroup";
import { LabelWrapper, GroupLabel } from "./styled";

const Checkbox3 = (props) => {
  const {
    id,
    extra = {},
    value = null,
    save = () => {},
    effects,
    runEffect,
    formData,
    validationError = "",
    disableValidation,
    htmlTemplates = {},
  } = props;
  let {
    name = "checkbox",
    label = "test",
    subLabel = "",
    options = [],
    optionsType = "static",
    effects: extraEffects,
    helpBoxAlwaysVisible = false,
    helpBoxIcon = false,
    helpBoxTemplateId = null,
    helpBoxTemplate = null,
    validation = {},
    appearance = "standard",
  } = extra;

  const { required = false } = validation;

  const [checkedValue, setCheckedValue] = useState(value);
  const [helpBoxOpen, setHelpBoxOpen] = useState(
    helpBoxAlwaysVisible ? true : false
  );

  const disabled = props.disabled || extra.disabled;

  let selectToDisplay = null;

  let error = validationError.critical ? "critical" : null;
  let errorMessage = validationError.critical;
  if (!error) {
    error = validationError.error ? "danger" : null;
    errorMessage = validationError.error;
  }
  if (!error) {
    error = validationError.warning ? "warning" : null;
    errorMessage = validationError.warning;
  }

  /**
   * MOUNT: Check for default values
   */
  useEffect(() => {
    if (
      value === null &&
      (optionsType === "static" || optionsType === "advanced")
    ) {
      let newValue = [];
      options.forEach((o) => {
        if (o.setDefault) {
          newValue.push(o.value ? o.value : o.label);
        }
      });
      if (newValue.length) {
        setCheckedValue(newValue);
      }
    }

    if (value === undefined || value === null) {
      if (
        (typeof value === "undefined" || value === null) &&
        extra.defaultStatic === true
      ) {
        const defaultValue = filterString(extra.staticValue, props, {});
        setCheckedValue(defaultValue);
      }
    }
  }, []);

  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current) {
      save({ key: id, value: checkedValue });
    } else {
      didMountRef.current = true;
    }
  }, [checkedValue]);

  useEffect(() => {
    if (
      (optionsType === "effects" || optionsType === "register") &&
      !isEmpty(extraEffects)
    ) {
      runEffect({ source: extraEffects, effectId: id });
    }
  }, [id, optionsType, runEffect, options, formData, extraEffects]);

  if (
    (optionsType === "effects" || optionsType === "register") &&
    !isEmpty(extraEffects)
  ) {
    selectToDisplay = get(effects, [id], null);
  } else {
    selectToDisplay = options;
  }

  const mountRef = useRef();

  // Update-effect ensure that we component updates when value change
  // 2022-08-24: This was added because radio did not update dynamiclly. After the implementation
  // we noticed the uncommented code below. WE ARE NOT SURE WHY WE UNCOMMENTED THE CODE BELOW.
  // If there is any strange behaviour with this component is is most likely this.
  useEffect(() => {
    if (mountRef.current) {
      if (value === null && extra.defaultStatic === true) {
        const defaultValue = filterString(extra.staticValue, props, {});

        setCheckedValue(defaultValue);
      } else {
        if (JSON.stringify(value) !== JSON.stringify(checkedValue)) {
          setCheckedValue(value);
        }
      }
    } else {
      mountRef.current = true;
    }
  }, [value]);

  /**
   * Checkbox has been checked
   */
  const onChange = useCallback(
    (v) => {
      setCheckedValue(
        produce((draft) => {
          if (!draft) {
            return [v];
          }

          /**
           * This is a backward-comp thing. Old tickets imported are sometimes set to true for checkbox values,
           * If this is the case, then we convert them to the new value whne cliekd
           */
          if (typeof draft === "boolean") {
            if (draft) {
              return null;
            } else {
              return [v];
            }
          }

          const i = draft.indexOf(v);

          if (i !== -1) {
            draft.splice(i, 1);
          } else {
            if (!Array.isArray(draft)) {
              return [v];
            }
            draft.push(v);
          }
        })
      );
    },
    [checkedValue]
  );

  const _helpBoxTemplate =
    helpBoxTemplateId === "_custom"
      ? helpBoxTemplate
      : get(htmlTemplates, [helpBoxTemplateId, "html"], "");

  return (
    <div className={required ? "sp-required" : ""}>
      <LabelWrapper>
        {extra.label && <GroupLabel>{label}</GroupLabel>}
        {!helpBoxAlwaysVisible && helpBoxIcon && (
          <HelpIcon toggle={() => setHelpBoxOpen(!helpBoxOpen)} />
        )}
      </LabelWrapper>
      {!selectToDisplay && <SkeletonLoader />}
      {selectToDisplay &&
        Array.isArray(selectToDisplay) &&
        (appearance === "horizontalButtons" ? (
          <HorizontalCheckboxGroup
            options={selectToDisplay}
            checkedValue={checkedValue}
            onChange={onChange}
            disabled={disabled}
            error={error}
          />
        ) : (
          selectToDisplay.map((line, i) => {
            let { label, value: v, setDisabled = false } = line;

            if (typeof line === "string") {
              label = line;
              v = line;
            }

            let _checkedValue =
              typeof checkedValue === "boolean"
                ? checkedValue
                : checkedValue &&
                  checkedValue.includes(v && v.length ? v : label);

            return (
              <Checkbox
                key={i}
                id={`${id}_${i}`}
                name={name}
                label={label}
                value={v}
                checked={_checkedValue}
                setCheckedValue={setCheckedValue}
                disabled={disabled || setDisabled}
                onChange={onChange}
                error={error}
              />
            );
          })
        ))}
      {(helpBoxAlwaysVisible || helpBoxIcon) &&
        _helpBoxTemplate &&
        helpBoxOpen && (
          <HelpBox template={_helpBoxTemplate} props={props} value={value} />
        )}
      <ValidationBar
        error={errorMessage}
        subLabel={subLabel}
        disableValidation={disableValidation}
      />
    </div>
  );
};

export default Checkbox3;
